
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";

const USER_ROLES = ["Student", "Professional", "Educator", "Researcher", "Creative", "Other"];

type FirstTimeOnboardingProps = {
  onComplete: () => void;
  onSkip: () => void;
};

export const FirstTimeOnboarding = ({ onComplete, onSkip }: FirstTimeOnboardingProps) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [userData, setUserData] = useState({
    fullName: user?.user_metadata?.full_name || "",
    avatarUrl: user?.user_metadata?.avatar_url || "",
    userRole: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const steps = [
    {
      title: "Welcome to Brain!",
      description: "Let's set up your profile to get started.",
      content: (
        <div className="space-y-4 py-4">
          <div className="flex justify-center mb-6">
            <Avatar className="w-24 h-24">
              {userData.avatarUrl ? (
                <AvatarImage src={userData.avatarUrl} alt={userData.fullName} />
              ) : (
                <AvatarFallback>{userData.fullName ? getInitials(userData.fullName) : "👤"}</AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fullName">Your Name</Label>
            <Input
              id="fullName"
              value={userData.fullName}
              onChange={(e) => setUserData({ ...userData, fullName: e.target.value })}
              placeholder="Enter your name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="avatarUrl">Profile Picture URL (optional)</Label>
            <Input
              id="avatarUrl"
              value={userData.avatarUrl}
              onChange={(e) => setUserData({ ...userData, avatarUrl: e.target.value })}
              placeholder="https://example.com/your-image.jpg"
            />
          </div>
        </div>
      ),
    },
    {
      title: "What best describes you?",
      description: "This helps us personalize your experience.",
      content: (
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="userRole">Select your role</Label>
            <Select
              value={userData.userRole}
              onValueChange={(value) => setUserData({ ...userData, userRole: value })}
            >
              <SelectTrigger id="userRole">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {USER_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ),
    },
    {
      title: "Key Features of Brain",
      description: "Here are some things you can do with Brain:",
      content: (
        <div className="space-y-6 py-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">📝</div>
            <div>
              <h3 className="font-medium">Create Rich Notes</h3>
              <p className="text-sm text-muted-foreground">Format your notes with rich text, images, and links</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-2xl">📂</div>
            <div>
              <h3 className="font-medium">Organize with Categories</h3>
              <p className="text-sm text-muted-foreground">Keep your notes organized by topic or purpose</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-2xl">⭐</div>
            <div>
              <h3 className="font-medium">Save Favorites</h3>
              <p className="text-sm text-muted-foreground">Mark important notes with the star icon</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-2xl">🔒</div>
            <div>
              <h3 className="font-medium">Password Protect</h3>
              <p className="text-sm text-muted-foreground">Add a password to sensitive notes to keep them secure</p>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (!user) return;
    
    setIsSubmitting(true);
    
    try {
      // Update the user profile in Supabase
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: userData.fullName,
          avatar_url: userData.avatarUrl,
          user_role: userData.userRole,
          onboarding_completed: true,
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      // Save to local storage for future reference
      localStorage.setItem(`onboarding-completed-${user.id}`, 'true');
      
      toast.success("Profile updated successfully");
      onComplete();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{steps[currentStep].title}</DialogTitle>
          <DialogDescription>{steps[currentStep].description}</DialogDescription>
        </DialogHeader>
        
        {steps[currentStep].content}
        
        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
                Back
              </Button>
            )}
            {currentStep === 0 && (
              <Button variant="ghost" onClick={onSkip} disabled={isSubmitting}>
                Skip
              </Button>
            )}
          </div>
          <Button 
            onClick={handleNext} 
            disabled={isSubmitting || (currentStep === 1 && !userData.userRole)}
          >
            {currentStep === steps.length - 1 ? (isSubmitting ? "Saving..." : "Complete") : "Next"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
